/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Button, Tab, Tabs } from '@mui/material';
import { ContainerLayout } from 'components/layouts/ContainerLayout';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import useSessionStore from 'stores/useSessionStore';
import { tabs } from 'components/layouts/Tab';
import UtMatrixListPopUp from './popup/UtMatrixListPopUp';
import { UtMatrixSummaryList } from 'models/ut/UtMatrixSummary';
import {
  getDetailSummaryData,
  getElectricSummaryData,
  getMachineSummaryData,
  getEquipmentResultData,
  getUtMatrixSummaryList,
} from 'apis/ut/UtMatrixSummary';
import DetailSummaryContent from './summary/DetailSummaryContent';
import MachineSummaryContent from './summary/MachineSummaryContent';
import ElectricSummaryContent from './summary/ElectricSummaryContent';
import { ComboBox } from 'components/selects/ComboBox';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import UtMatrixGraphPopup from './popup/UtMatrixGraphPopup';
import { CommonUtil } from 'utils/CommonUtil';
import { DefaultZeroNumberCellTemplate } from 'components/grids/NumberCellRenderer';
import EquipmentResultContent from './summary/EquipmentResultContent';
import { getExcelFileName } from 'utils/ExcelUtil';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import GridSettingButtons from 'components/grids/GridSettingButtons';

interface SearchParam {
  totalStandard?: string;
}

/**
 * @desc UT Matrix 결과보기
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.02   jshwang12       자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리)
 * 2025.10.03   jshwang12       [고도화] 설비별 결과 탭 추가
 * 2025.10.04   jshwang12       UT Matrix Summary 그리드 > N2 AVG 추가
 * 2025.11.05   jihwan89        CSR E20250627-001556-6/27 UT Matrix 화면 개선요청 (결과보기, 결재화면, 작성화면, list화면) summary 엑셀다운로드 추가
 */
const UtMatrixSummaryPage = () => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const navigate = useNavigate();
  const machineRef = useRef<any>();
  const elecRef = useRef<any>();
  const detailRef = useRef<any>();
  const eqpRef = useRef<any>();
  const userSession = useSessionStore();
  const location = useLocation();
  const state = { ...location.state };

  const gridRef2 = useRef<any>();
  const [isChangeColumn, setIsChangeColumn] = useState<boolean>(false);

  const [hitTest, setHitTest] = useState<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const [flexItem, setFlexItem] = useState<any>();

  const [summaryRowData, setSummaryRowData] = useState<UtMatrixSummaryList[]>([]);
  const [machineRowData, setMachineRowData] = useState<any>();
  const [electricRowData, setElectricRowData] = useState<any>();
  const [eqpResultRowData, setEqpResultRowData] = useState<any>(); // [25.10.03][고도화] 설비별 결과 탭 추가
  const [detailRowData, setDetailRowData] = useState<any>();

  const [rowDataChange, setRowDataChange] = useState<boolean>(false);

  const [utmData, setUtmData] = useState<UtMatrixSummaryList[]>([]);

  const [isOpenModal, setOpenModal] = useState<boolean>(false);
  const [isOpenGraphModal, setOpenGraphModal] = useState<boolean>(false);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const tabMenus = useMemo(() => {
    return [
      { id: 'MACH', name: t('ut.label.기계요약', '기계요약') },
      { id: 'ELTR', name: t('ut.label.전기요약', '전기요약') },
      { id: 'EQP', name: t('ut.label.설비별 결과', '설비별 결과') },
      { id: 'UTM', name: t('ut.label.상세보기', '상세보기') },
    ];
  }, []);

  const [searchParam, setSearchParam] = useState<SearchParam>({
    totalStandard: 'ALL',
  });
  const [code, setCode] = useState<any>();

  useEffect(() => {
    getCommonCodesForGrid();
  }, []);

  const getCommonCodesForGrid = async () => {
    const sumBaseCd: Code[] = await getCommonCodeNames('SUM_BASE_CD');

    setCode({
      sumBaseCd: sumBaseCd,
    });
  };

  useEffect(() => {
    if ((state.utmData || []).length > 0) {
      callbackUtmData(state?.utmData);
    }
  }, [state.utmData]);

  useEffect(() => {
    if (summaryRowData.length > 0) {
      btnShowResult();
    }
  }, [rowDataChange]);

  const fnSearchJudgeResultBtn = (params: any) => {
    const searchParam = {
      gatingId: params.gatingId,
      gatingNo: params.gatingNo,
    };
    navigate('/gating/result/gating-judge-report', { state: { sJudgeResultParam: searchParam } });
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        isReadOnly: true,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'utmId',
        header: String(t('ut.label.UTM ID', 'UTM ID')),
        minWidth: 70,
        width: 95,
        visible: true,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'bildPlntCdNm',
        header: String(t('ut.label.공장', '공장')),
        minWidth: 70,
        width: 100,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'bildPlntCd',
        visible: false,
      },
      // {
      //   binding: 'askPurpCdNm',
      //   header: String(t('ut.label.목적', '목적')),
      //   width: 100,
      //   align: 'center',
      //   isReadOnly: true,
      // },
      {
        binding: 'askPurpCd',
        visible: false,
      },
      {
        binding: 'utmNo',
        header: String(t('ut.label.UT Matrix 번호', 'UT Matrix 번호')),
        minWidth: 70,
        width: 125,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'utmNm',
        header: String(t('ut.label.UT Matrix 명', 'UT Matrix 명')),
        minWidth: 70,
        width: 180,
        isReadOnly: true,
        // cssClass: 'WijmoFind',
        // cellTemplate: '<span>${item.utmNm}</span><button id="btnOpenModal" />',
      },
      {
        binding: 'verNo',
        header: String(t('ut.label.버전', '버전')),
        minWidth: 60,
        width: 65,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'lineQty',
        header: String(t('ut.label.Line 수', 'Line 수')),
        minWidth: 70,
        width: 85,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'graph',
        header: String(t('ut.label.Graph', 'Graph')),
        minWidth: 53,
        width: 53,
        align: 'center',
        isReadOnly: true,
        cssClass: 'WijmoPlay',
        cellTemplate: '<button id="btnMoveGraph"></button>',
      },
      {
        header: String(t('ut.label.전기', '전기')),
        columns: [
          {
            binding: 'elec',
            header: '[kW]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.CDA', 'CDA')),
        columns: [
          {
            binding: 'cda',
            header: '[Nl/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.N2 Peak', 'N2 Peak')),
        columns: [
          {
            binding: 'n2',
            header: '[Nl/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.N2 AVG', 'N2 AVG')),
        columns: [
          {
            binding: 'n2Avg',
            header: '[Nl/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.IW', 'IW')),
        columns: [
          {
            binding: 'iw',
            header: '[ℓ/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.FW', 'FW')),
        columns: [
          {
            binding: 'iw2',
            header: '[ℓ/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.PCW', 'PCW')),
        columns: [
          {
            binding: 'pcw',
            header: '[ℓ/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.WW', 'WW')),
        columns: [
          {
            binding: 'ww',
            header: '[ℓ/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.Steam', 'Steam')),
        columns: [
          {
            binding: 'steam',
            header: '[t/hr]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.NG', 'NG')),
        columns: [
          {
            binding: 'ng',
            header: '[N㎥/hr]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.Return air', 'Return air')),
        columns: [
          {
            binding: 'exRetrun',
            header: '[CMH]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.Exhaust air', 'Exhaust air')),
        columns: [
          {
            binding: 'exVent',
            header: '[CMH]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.Supply Air', 'Supply air')),
        columns: [
          {
            binding: 'sa',
            header: '[CMH]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
    ];
  }, [isChangeColumn, flexRef]);

  const onInitialized = (grid) => {
    setFlexRef(grid);
    grid.loadedRows.addHandler(() => {
      const items = grid.collectionView ? grid.collectionView.sourceCollection : grid.itemsSource;
      if (!items || !Array.isArray(items)) {
        return;
      }

      items.forEach((item, rowIndex) => {
        grid.columns.forEach((col, colIndex) => {
          const value = grid.getCellData(rowIndex, colIndex, false);
          const content = CommonUtil.safeHtmlContents(value, false);

          grid.setCellData(rowIndex, colIndex, content, true);
        });
      });
      grid.invalidate();
    });

    grid.hostElement.addEventListener('click', (e) => {
      const len = grid.rows.length;
      if (len == 0) return;
      const ht = grid.hitTest(e);

      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return; // 선택된 영역이 셀이 아니면 나가리.

      if (e.target instanceof HTMLButtonElement) {
        const col = grid.columns[ht.col]; // 선택한 컬럼의 정보
        const item = grid.rows[ht.row].dataItem; // 선택한 row의 정
        setFlexItem(item);
        setHitTest(ht);
        switch (e.target.id) {
          case 'btnOpenModal':
            setOpenModal(true);
            break;
          case 'btnMoveGraph':
            setOpenGraphModal(true);
            setUtmData(item);
            break;
        }
      }
    });

    grid.refresh();
  };

  const callbackUtmData = (utmData) => {
    const utmIds = utmData.map((item) => item.utmId).join(',');

    getUtMatrixSummaryList(utmIds).then((result: UtMatrixSummaryList[]) => {
      if ((result || []).length > 0) {
        const utmData: any = [];

        if (result.length > 10) {
          // utmId 10개 이상 선택 시 10개만 등록하고 메세지로 10개 이상 등록할 수 없습니다.
          const sortUtmId = _.sortBy(result, ['utmId']);
          for (let i = 0; i < 10; i++) {
            utmData.push(sortUtmId[i]);
          }
          setSummaryRowData(utmData);
          openMessageBar({
            type: 'warning',
            content: t(
              'ut.label.Ut Matrix를 추가 할 수 없습니다. (최대 10개)',
              'Ut Matrix를 추가 할 수 없습니다. (최대 10개)'
            ),
          });
        } else if (result.length + summaryRowData.length > 10) {
          // 기존 utmId + new 10개 이상시 utmId 순서대로 10개까지만 등록하고 10개 이상 등록할 수 없습니다.
          const sortUtmId = _.sortBy(result, ['utmId']);
          for (let i = 0; i < 10 - summaryRowData.length; i++) {
            utmData.push(sortUtmId[i]);
          }
          // const newRow = summaryRowData.concat(result);
          const newRow = _.uniqBy(summaryRowData?.concat(utmData), 'utmId');
          setSummaryRowData(newRow);
        } else {
          const newRow = _.uniqBy(summaryRowData?.concat(result), 'utmId');
          const sortUtmId = _.sortBy(newRow, ['utmId']);
          setSummaryRowData(sortUtmId);
        }
        setRowDataChange(rowDataChange ? false : true);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const btnDelRow = () => {
    const selectedRowNodes = flexRef.selectedRows;

    const isSelected = selectedRowNodes.filter((item) => item.isSelected);

    if (isSelected.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('ut.label.Ut Matrix를 선택해 주세요.', 'Ut Matrix를 선택해 주세요.'),
      });
      return false;
    }
    const selectedIds = selectedRowNodes
      .map((rowNode) => {
        return parseInt(rowNode._idx!);
      })
      .reverse();

    selectedIds.forEach((idx) => {
      delete summaryRowData[idx];
    });
    const filteredData = summaryRowData.filter((element) => element !== undefined);
    const sortUtmId = _.sortBy(filteredData, ['utmId']);
    setSummaryRowData(sortUtmId);
    setRowDataChange(rowDataChange ? false : true);
  };

  const btnShowResult = () => {
    if ((summaryRowData || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('ut.label.UT Matrix를 추가해 주세요.', 'UT Matrix를 추가해 주세요.'),
      });
      return false;
    }
    const utmIds = summaryRowData.map((item) => item.utmId).join(',');
    getMachineSummaryData(utmIds, searchParam).then((result) => {
      if (result !== null) {
        setMachineRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
    getElectricSummaryData(utmIds, searchParam).then((result) => {
      if (result !== null) {
        setElectricRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
    getEquipmentResultData(utmIds, searchParam).then((result) => {
      if (result !== null) {
        setEqpResultRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
    getDetailSummaryData(utmIds).then((result) => {
      if (result !== null) {
        setDetailRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const btnExcelDownload = () => {
    if (currentTabIndex === 0) {
      machineRef.current?.btnMachineExcelExport();
    } else if (currentTabIndex === 1) {
      elecRef.current?.btnElecExcelExport();
    } else if (currentTabIndex === 2) {
      // [25.10.03][고도화] 설비별 결과 탭 추가
      eqpRef.current?.btnDetailExcelExport();
    } else if (currentTabIndex === 3) {
      detailRef.current?.btnDetailExcelExport();
    }
  };

  const btnMasterExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = 'Ut Matrix Summary';
    book.saveAsync(getExcelFileName(t('ut.label.UT Matrix Summary', 'UT Matrix Summary')));
  };

  return (
    <ContainerLayout>
      <SubTitleLayout style={{ marginTop: '0' }}>
        <SubTitleGroup>
          <h3>{t('ut.label.UT Matrix Summary', 'UT Matrix Summary')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(summaryRowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="addRow"
            onClick={() => setOpenModal(true)}
            disableRipple
          >
            {t('com.button.행추가', '행추가')}
          </Button>
          <Button css={IconButton.button} className="delRow" onClick={btnDelRow} disableRipple>
            {t('com.button.행삭제', '행삭제')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnMasterExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <GridSettingButtons
            gridId={'utMatrixSummaryResultGrid'} // <CustomGrid>에 선언한 id와 동일한 값
            layoutDefinition={layoutDefinition} // 그리드 레이아웃
            changeShownColumns={(result) => {
              // 노출필드 변경이벤트
              // ref를 이용해 <CustomGrid> 내 함수를 실행
              //openLoading(true);
              gridRef2.current?.changeColumn?.(result);
              setIsChangeColumn(true);
              flexRef.invalidate();
              //gridRef.current.refresh();
              //openLoading(false);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={'utMatrixSummaryResultGrid'}
        ref={gridRef2}
        layoutDefinition={layoutDefinition}
        rowData={summaryRowData}
        initialized={onInitialized}
        showMarquee={true}
        height={130}
        isFilter={false}
        excludePin={[
          'graph',
          'elec',
          'cda',
          'n2',
          'n2Avg',
          'iw',
          'iw2',
          'pcw',
          'ww',
          'steam',
          'ng',
          'exRetrun',
          'exVent',
          'sa',
        ]}
      />
      <SubTitleLayout style={{ marginTop: '28px' }}>
        <SubTitleGroup>
          <div css={tabs.wrap} style={{ marginTop: '24px' }}>
            <Tabs
              value={currentTabIndex}
              onChange={(e, newValue) => setCurrentTabIndex(newValue)}
              css={tabs.tab}
            >
              {tabMenus.map((o, idx) => (
                <Tab
                  key={o.id}
                  label={o.name}
                  id={`ut-tab-${idx}`}
                  aria-controls={`ut-tabpanel-${idx}`}
                />
              ))}
            </Tabs>
          </div>
          <span className="total">
            {String(t('com.label.총', '총'))}
            <span>
              {(
                (currentTabIndex === 0
                  ? machineRowData?.list
                  : currentTabIndex === 1
                  ? electricRowData?.list
                  : detailRowData) || []
              ).length.toLocaleString()}
            </span>
            {String(t('com.label.건', '건'))}
          </span>
        </SubTitleGroup>

        <ControlBtnGroup>
          <label>{String(t('ut.label.집계기준', '집계기준'))}</label>
          <ComboBox
            options={code?.sumBaseCd}
            defaultValue={searchParam.totalStandard}
            onChange={(value) => {
              setSearchParam({
                ...searchParam,
                totalStandard: value,
              });
            }}
          />
          <Button css={IconButton.button} className="request" onClick={btnShowResult} disableRipple>
            {String(t('com.button.결과보기', '결과보기'))}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelDownload}
            disableRipple
          >
            {String(t('com.button.다운로드', '다운로드'))}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <TabPanel value={currentTabIndex} index={currentTabIndex}>
        {currentTabIndex === 0 ? (
          <MachineSummaryContent ref={machineRef} machineRowData={machineRowData} />
        ) : currentTabIndex === 1 ? (
          <ElectricSummaryContent ref={elecRef} electricRowData={electricRowData} />
        ) : currentTabIndex === 2 ? (
          <EquipmentResultContent ref={eqpRef} eqpResultRowData={eqpResultRowData} />
        ) : currentTabIndex === 3 ? (
          <DetailSummaryContent ref={detailRef} detailRowData={detailRowData} />
        ) : (
          ''
        )}
      </TabPanel>
      {isOpenModal && (
        <UtMatrixListPopUp
          open={isOpenModal}
          close={() => setOpenModal(false)}
          onCallback={(callbackData) => callbackUtmData(callbackData)}
        />
      )}
      {isOpenGraphModal && (
        <UtMatrixGraphPopup
          open={isOpenGraphModal}
          close={() => setOpenGraphModal(false)}
          condition={utmData}
        />
      )}
    </ContainerLayout>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`ut-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default UtMatrixSummaryPage;
